import React, { useEffect, useMemo, useState } from 'react';
import { Info } from '@mui/icons-material';

import { getColorFromTurb } from '../utilities/mapHelperFunctions/turbHelperFunctions';
import {
  getDepartureTime,
  getArrivalTime,
  timeConvert,
  hoursToMinutes,
} from '../utilities/helperfunctions';
import { useWindowDimensions } from '../hooks';
import { InfoModal } from './InfoModal';

export const TurbulenceIndicator = ({ data, isCabinCrew }) => {
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  const { width: windowWidth } = useWindowDimensions();

  const handleOpenInfoModal = () => setIsInfoModalOpen(true);
  const handleCloseInfoModal = () => setIsInfoModalOpen(false);

  const turbInfo = useMemo(() => {
    return (data?.WP_List?.[0].WayPoint || []).map((currentWayPoint, index) => {
      const flightTime = data?.dbEstFlightTime?.[0] || 1;
      const name = currentWayPoint.sWaypointName?.[0] || 'N/A';

      const prevWayPoint = data?.WP_List?.[0].WayPoint?.[index - 1];

      const prevTurb = prevWayPoint?.sSegmentShearRate?.[0]?.[0] || 0;
      const currTurb = currentWayPoint.sSegmentShearRate?.[0]?.[0] || 0;

      const prevColor = getColorFromTurb(prevTurb);
      const currColor = getColorFromTurb(currTurb);

      const currTurbTime = currentWayPoint.dbEstCumulatedFlightTime_H?.[0] || 0;
      const currFraction = (currTurbTime / flightTime) * 100;

      const prevTurbTime = prevWayPoint?.dbEstCumulatedFlightTime_H?.[0] || 0;
      const elapsedTime = currentWayPoint.dbEstCumulatedFlightTime_H?.[0] || 0;
      const prevFraction = (prevTurbTime / flightTime) * 100;

      const width = Math.floor((currFraction - prevFraction) * 100) / 100;
      const left = prevFraction || 0;
      const center = left + width / 2;

      return {
        name,
        currTurb,
        elapsedTime,
        prevTurbTime,
        prevColor,
        currColor,
        width,
        left,
        center,
        currFraction,
      };
    });
  }, [data]);

  const filteredTurbInfo = useMemo(() => {
    return turbInfo.filter(
      (turb) => turb.prevColor !== 'red' && turb.currColor === 'red'
    );
  }, [turbInfo, windowWidth]);

  const highestTurbInfo = useMemo(() => {
    const highestTurbInfo = [];
    let currentGroup = [];

    turbInfo.map((turb, index) => {
      // A new turbulence "group" is defined by color not being red, function is responsible for finding the highest tubulence value within the group
      if (turb.currColor !== 'red') {
        if (currentGroup.length) {
          const highestTurb = currentGroup.reduce((max, obj) =>
            max.currTurb > obj.currTurb ? max : obj
          );
          highestTurbInfo.push(highestTurb);
          currentGroup = [];
        }
      } else {
        currentGroup.push({ ...turb, index });
      }
    });

    if (currentGroup.length > 0) {
      const highestTurb = currentGroup.reduce((max, obj) =>
        max.currTurb > obj.currTurb ? max : obj
      );
      // Highest turbulence value within each red area is pushed to array
      highestTurbInfo.push(highestTurb);
    }

    return highestTurbInfo;
  }, [turbInfo]);

  useEffect(() => {
    // reposition shear data to stop overlapping elements
    if (highestTurbInfo.length) {
      const turbulenceIndicator = document.getElementById(
        'turbulenceIndicator'
      );
      highestTurbInfo.reduce((prevRight, curr) => {
        const halfShearWidth = 34;
        const spacing = 10;

        const center = turbulenceIndicator.offsetWidth * (curr.center / 100);
        const right = center + halfShearWidth;
        const left = center - halfShearWidth;

        // if previous right side goes beyond current left side elements are overlapping
        if (prevRight && prevRight > left) {
          const element = document.getElementById(`highestShear${curr.index}`);
          if (element) {
            const leftOffset = prevRight - left - halfShearWidth + spacing;
            element.style.left = `${leftOffset}px`;
            return right + leftOffset + halfShearWidth;
          }
        }
        return right;
      }, 0);
    }
  }, [highestTurbInfo, window]);

  const departureTime = getDepartureTime(data || '') || 'N/A';
  const arrivalTime = getArrivalTime(data || '') || 'N/A';
  const flightTime =
    data?.dbEstFlightTime?.[0] && Number(data?.dbEstFlightTime?.[0]) > 0
      ? timeConvert(Number(data.dbEstFlightTime[0]))
      : 'N/A';

  const flightTimeMinutes = hoursToMinutes(
    Number(data?.dbEstFlightTime?.[0] || '0')
  );

  const halfHourBlocks = Math.floor(flightTimeMinutes / 30);

  return (
    <>
      <InfoModal isOpen={isInfoModalOpen} handleClose={handleCloseInfoModal} />

      <div
        className="mediumBoldText"
        style={{
          position: 'absolute',
          display: 'flex',
          flexDirection: 'column',
          margin: 'unset auto',
          width: '100%',
          height: 'fit-content',
          padding: '10px 30px',
          zIndex: 2,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <p>{isCabinCrew ? data?.sDepIATA : data?.sDepICAO}</p>
          <p>Flight Time</p>
          <p>{isCabinCrew ? data?.sDestIATA : data?.sDestICAO}</p>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <p>{departureTime}</p>
          <p>{flightTime}</p>
          <p>{arrivalTime}</p>
        </div>

        <div
          id="turbulenceIndicator"
          style={{
            display: 'flex',
            flexDirection: 'row',
            marginTop: '4px',
            width: '100%',
            height: 'fit-content',
            position: 'relative',
          }}
        >
          {turbInfo.map(
            (
              { width, prevColor, currColor, currTurb, name, elapsedTime },
              index
            ) => {
              const highestShear = highestTurbInfo.find(
                (turb) => turb.index === index
              );

              return (
                <div
                  style={{
                    width: `${width}%`,
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  key={index}
                >
                  <div
                    style={{
                      width: `100%`,
                      height: '30px',
                      borderBottom: 'solid 2px black',
                      backgroundImage: `linear-gradient(to right, ${prevColor}, ${currColor})`,
                    }}
                  />
                  {highestShear && (
                    <div
                      id={`highestShear${index}`}
                      style={{
                        position: 'absolute',
                        fontSize: '10.4px',
                        width: 'fit-content',
                        whiteSpace: 'nowrap',
                        textAlign: 'center',
                        zIndex: 1,
                      }}
                    >
                      <p>MaxShear: {currTurb}</p>
                      <p>{name}</p>
                    </div>
                  )}
                </div>
              );
            }
          )}
          {Array.from(Array(halfHourBlocks).keys()).map((_, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                bottom: 0,
                left: `calc((${30} / ${flightTimeMinutes}) * (100% * ${
                  i + 1
                }))`,
                borderRight: 'solid 2px black',
                height: '7px',
              }}
            />
          ))}
          <div
            onClick={handleOpenInfoModal}
            className="centered"
            style={{
              cursor: 'pointer',
              position: 'absolute',
              right: 0,
              top: 0,
              width: 'fit-content',
              zIndex: 100,
            }}
          >
            <Info fontSize="small" style={{ marginRight: 5, marginTop: 3 }} />
          </div>
        </div>
        <div style={{ display: 'flex', position: 'relative' }}>
          {filteredTurbInfo.map((turb, index) => {
            const turbulenceIndicator = document.getElementById(
              'turbulenceIndicator'
            );
            const prevTimeslotContainer = document.getElementById(
              `timeslotContainer${index - 1}`
            );
            const prevTimeslot = document.getElementById(
              `timeslot${index - 1}`
            );
            const prevTimeslotLine = document.getElementById(
              `timeslotLine${index - 1}`
            );

            const left = turb.currFraction - turb.width;

            const prevLeft = Number(
              prevTimeslotContainer?.style?.left?.match(/(\d+)/)?.[0] || '0' // filters out non-numbers such as 'px'
            );

            const timeslotPercentageWidth = Math.round(
              (prevTimeslot?.offsetWidth / turbulenceIndicator?.offsetWidth) *
                100
            );

            const lineHeight =
              index !== 0 && prevLeft + timeslotPercentageWidth + 2 >= left // 2 is the offset to have the line height increase slightly before the two timeslots meet
                ? prevTimeslotLine.offsetHeight + prevTimeslot.offsetHeight
                : 5;

            return (
              <div
                id={`timeslotContainer${index}`}
                key={index}
                style={{
                  position: 'absolute',
                  left: `${left}%`,
                }}
              >
                <div
                  id={`timeslotLine${index}`}
                  style={{
                    width: 2,
                    height: lineHeight,
                    marginBottom: 2,
                    backgroundColor: 'black',
                  }}
                />
                <div
                  id={`timeslot${index}`}
                  style={{
                    transform: 'translate(-50%)',
                  }}
                >
                  <p>{timeConvert(turb.elapsedTime)}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};
